import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Paper,
  styled,
  useTheme
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getBlogPostBySlug, getBlogPostsByCategory, getComments, addComment } from '../utils/blogUtils';
import type { BlogPost, Comment } from '../types/blog';
import ReactMarkdown from 'react-markdown';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';

// Styled components for markdown content
const MarkdownContent = styled('div')(({ theme }: { theme: Theme }) => ({
  maxWidth: 'none',
  '& pre': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflowX: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
      fontSize: '1rem',
      fontFamily: '"Roboto Mono", monospace',
    }
  },
  '& code': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    padding: '2px 6px',
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    fontFamily: '"Roboto Mono", monospace',
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: theme.palette.text.primary,
    margin: 0,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  '& h1': {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  '& h2': {
    fontSize: '2rem',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  '& h3': {
    fontSize: '1.75rem',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
    paddingLeft: theme.spacing(2),
    fontStyle: 'italic',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  '& p': {
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
  },
  '& strong': {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  }
}));


export default function BlogPostComponent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [categoryPosts, setCategoryPosts] = useState<BlogPost[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const loadPost = async () => {
      if (slug) {
        const result = await getBlogPostBySlug(slug);
        if (result) {
          setPost(result);
          // First load category posts
          const catPosts = await getBlogPostsByCategory(result.category);
          // Filter to ensure only posts from the same category
          const filteredPosts = catPosts.filter(p => p.category === result.category);
          setCategoryPosts(filteredPosts);
        }
      }
      // Load comments
      if (slug) {
        const commentData = await getComments(slug);
        setComments(commentData);
      }
    };
    loadPost();
  }, [slug]);

  const handleBackClick = () => {
    if (post) {
      navigate(`/category/${post.category}`);
    }
  };

  const handlePostSwitch = (targetSlug: string) => {
    if (targetSlug !== slug && post) {
      const targetPost = categoryPosts.find(p => p.slug === targetSlug);
      if (targetPost && targetPost.category === post.category) {
        navigate(`/post/${targetSlug}`);
        if (isMobile) setDrawerOpen(false);
      }
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = commentInput.trim();
    if (!trimmed || !slug) return;
    
    try {
      const name = anonymous || !nameInput.trim() ? 'Anonymous' : nameInput.trim();
      const newComment = await addComment(slug, name, trimmed);
      setComments(prevComments => [newComment, ...prevComments]);
      setCommentInput('');
      if (!anonymous) setNameInput('');
    } catch (error) {
      console.error('Failed to add comment:', error);
      // You might want to show an error message to the user here
    }
  };

  if (!post) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  // Sidebar content
  const sidebar = (
    <Box sx={{ width: 260, pt: 2 }}>
      <Typography variant="h6" sx={{ px: 2, mb: 1 }}>
        Other posts in {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
      </Typography>
      <List>
        {categoryPosts.map((p) => (
          <ListItem
            button
            key={p.slug}
            selected={p.slug === slug}
            disabled={p.slug === slug}
            onClick={() => handlePostSwitch(p.slug)}
          >
            <ListItemText primary={p.title} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'row' }}>
        {/* Sidebar for desktop, Drawer for mobile */}
        {isMobile ? (
          <>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ mr: 2 }}>
              <svg width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M4 6h16M4 12h16M4 18h16"/></svg>
            </IconButton>
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              {sidebar}
            </Drawer>
          </>
        ) : (
          <Box sx={{ minWidth: 260, mr: 4 }}>{sidebar}</Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton 
              onClick={handleBackClick}
              sx={{ mr: 2 }}
              aria-label="back to category"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {post.title}
            </Typography>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" color="text.secondary">
              Category: {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {new Date(post.date).toLocaleDateString()}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
            }}
          >
            <MarkdownContent theme={theme}>
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </MarkdownContent>
          </Paper>

          {/* About the Author Section */}
          <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, mt: 4, mb: 4, bgcolor: 'background.paper' }}>
            <ButtonBase
              onClick={() => navigate('/about')}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left', borderRadius: 2, p: 0, ':hover': { bgcolor: 'action.hover' } }}
              focusRipple
            >
              <Avatar sx={{ width: 56, height: 56 }}>A</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, cursor: 'pointer' }}>Alex Writer</Typography>
                <Typography variant="body2" color="text.secondary">
                  Alex is a passionate blogger and tech enthusiast who loves sharing knowledge about AI, travel, and productivity. When not writing, Alex enjoys hiking and exploring new places.
                </Typography>
              </Box>
            </ButtonBase>
          </Paper>

          {/* Comments Section */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Comments
            </Typography>
            <Box component="form" onSubmit={handleCommentSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Add a comment"
                  variant="outlined"
                  size="small"
                  value={commentInput}
                  onChange={e => setCommentInput(e.target.value)}
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={!commentInput.trim()}>
                  Post
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Your name"
                  variant="outlined"
                  size="small"
                  value={anonymous ? '' : nameInput}
                  onChange={e => { setNameInput(e.target.value); setAnonymous(false); }}
                  disabled={anonymous}
                  sx={{ maxWidth: 220 }}
                  placeholder="Anonymous"
                />
                <Button
                  variant={anonymous ? 'contained' : 'outlined'}
                  onClick={() => setAnonymous(a => !a)}
                  sx={{ minWidth: 120 }}
                >
                  {anonymous ? 'Posting as Anonymous' : 'Post as Anonymous'}
                </Button>
              </Box>
            </Box>
            <List>
              {comments.length === 0 && (
                <Typography color="text.secondary" sx={{ px: 2 }}>
                  No comments yet. Be the first to comment!
                </Typography>
              )}
              {comments.map((c, i) => (
                <ListItem key={i} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>{c.name && c.name !== 'Anonymous' ? c.name[0].toUpperCase() : '?'}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c.text}
                    secondary={c.name}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}